import React from 'react'
import { NavLink } from 'react-router-dom'
import { ROUTES } from '../../../../../constants/routes'
import { formatFullName, formatList } from '../../../../../utils/formatters'
import { BatchImportOperation, ImportItemType } from '../hooks/useHandleImport'

interface IProps {
  itemTypes: ImportItemType[]
  operation: BatchImportOperation | undefined
  onRetry: () => void
  onClear: () => void
}

export const CompleteImportStep = ({
  operation,
  itemTypes,
  onClear: handleClear,
  onRetry: handleRetry,
}: IProps) => {
  if (
    !operation ||
    (operation.errored_operations === operation.total_operations &&
      operation.errored_operations > 0)
  ) {
    return (
      <div className="py-3">
        <h5 className="mb-4">Your imported data is invalid or already exists</h5>
        <p>We have not added any data to your Citrus program.</p>
        <ImportLinks operation={operation} onClear={handleClear} onRetry={handleRetry} />
      </div>
    )
  }

  if (
    operation.errored_operations < operation.total_operations &&
    operation.errored_operations > 0
  ) {
    return (
      <div className="py-3">
        <h5 className="mb-4">Some of your imported data are invalid or already exists</h5>
        <p>
          We were only able to add{' '}
          <b>
            {operation.finished_operations} of {operation.total_operations}
          </b>{' '}
          to your Citrus data, the following failed to import:
        </p>
        <ul className="list-unstyled">
          {operation?.attendees?.total_operations && operation?.attendees?.total_operations > 0 && (
            <>
              <li>Attendees</li>
              <ul>
                {operation.attendees.items
                  .filter((i) => !i.successful)
                  .map((i) => (
                    <li key={`failed-${i.id}`}>
                      {formatFullName(i.data) || 'Unknown attendee'} failed to import due to:{' '}
                      {i.error_reason?.split('_').join(' ') || 'Unknown reason'}.
                    </li>
                  ))}
              </ul>
            </>
          )}
          {operation?.contacts?.total_operations && operation?.contacts?.total_operations > 0 && (
            <>
              <li>Contacts</li>
              <ul>
                {operation.contacts.items
                  .filter((i) => !i.successful)
                  .map((i) => (
                    <li key={`failed-${i.id}`}>
                      {formatFullName(i.data) || 'Unknown attendee'} failed to import due to:{' '}
                      {i.error_reason?.split('_').join(' ') || 'Unknown reason'}.
                    </li>
                  ))}
              </ul>
            </>
          )}
        </ul>
        <ImportLinks operation={operation} onClear={handleClear} />
      </div>
    )
  }

  return (
    <div className="py-3">
      <h5 className="mb-4">We successfully imported your attendees</h5>
      <p>
        We have successfully added{' '}
        <b>
          {operation.finished_operations} of {operation.total_operations} {formatList(itemTypes)}
        </b>{' '}
        to your Citrus program.
      </p>
      <ImportLinks operation={operation} onClear={handleClear} />
    </div>
  )
}

type IImportLinksProps = Pick<IProps, 'onClear' | 'operation'> & Partial<Pick<IProps, 'onRetry'>>

const ImportLinks = ({
  operation,
  onClear: handleClear,
  onRetry: handleRetry,
}: IImportLinksProps) => (
  <div className="mb-3 d-flex mt-4">
    <NavLink
      className="btn btn-primary btn-block d-md-inline-block w-md-auto mt-2"
      to={ROUTES.DASHBOARD}
    >
      Return to dashboard
    </NavLink>
    {operation?.attendees?.total_operations && operation?.attendees?.total_operations > 0 && (
      <NavLink
        className="btn btn-link mx-md-2 btn-block d-md-inline-block w-md-auto mt-2"
        to={ROUTES.ATTENDEES}
      >
        Return to attendees
      </NavLink>
    )}
    {operation?.contacts?.total_operations && operation?.contacts?.total_operations > 0 && (
      <NavLink
        className="btn btn-link mx-md-2 btn-block d-md-inline-block w-md-auto mt-2"
        to={ROUTES.CONTACTS}
      >
        Return to contacts
      </NavLink>
    )}
    <button
      type="button"
      className="btn btn-link mx-md-2 btn-block d-md-inline-block w-md-auto mt-2"
      onClick={() => handleClear()}
    >
      Import more data
    </button>
    {handleRetry && (
      <button
        type="button"
        className="btn btn-link mx-md-2 btn-block d-md-inline-block w-md-auto mt-2"
        onClick={() => handleRetry()}
      >
        Try again
      </button>
    )}
  </div>
)
